/* ============================================================================
   ANCONA APEX V5 — GLOBAL SHELL SCRIPT (ancona-v5-shell.js)
   Agent A (2026-07-28). The ONE global custom JS request. Classic script,
   loaded with defer from layout/theme.liquid.

   Provides (window.AnconaV5):
   - bus        event-bus helper over document CustomEvents
   - trapFocus  the single focus-trap implementation for all V5 dialogs
   - announce   polite/assertive live-region announcer
   - dialog     manager for [data-av5-dialog] roots ([data-av5-dialog-open] /
                [data-av5-dialog-close] triggers, Escape, backdrop, focus
                return, scroll lock)
   - presentationPolicy  fail-closed consumer of the server-emitted product-ID
                exclusion JSON
   - loadClientBook  deferred, non-blocking load of ancona-v5-client-book.js on
                Client Book/Compare interaction or the direct Client Book page
   - loadConcierge  deferred Section Rendering markup + CSS/script load on
                explicit launcher interaction
   - recent tracking  a shell-local, schema-bounded product-context update;
                recording a product view does not load the Client Book bundle
   - metrics    window.dataLayer init + republish of the public `ancona:*`
                CustomEvent vocabulary to Shopify.analytics.publish (event
                names carried over from V4 ancona-metrics.js unchanged)

   Sticky header: native Dawn <sticky-header> (sections/header.liquid) remains
   the authority — no custom sticky logic here.
   Idempotent: guarded singleton; all wiring is delegated document-level
   listeners, so shopify:section:load re-renders need no re-binding. Header
   badge counts are refreshed on shopify:section:load per the P0-8 pattern.
   Storage-denied: every localStorage touch is inside try/catch; failures
   degrade to hidden badges and an unavailability announcement from the
   Client Book module itself.
   ============================================================================ */
(function () {
  'use strict';
  if (window.AnconaV5) return;

  var shellScript = document.currentScript;
  var SCRIPT_SRC = (shellScript && shellScript.src) || '';
  var CLIENT_BOOK_SRC = (shellScript && shellScript.dataset.av5ClientBookSrc) || '';
  var CONCIERGE_SRC = (shellScript && shellScript.dataset.av5ConciergeSrc) || '';
  var CONCIERGE_CSS = (shellScript && shellScript.dataset.av5ConciergeCss) || '';
  var TRACK_RECENT = !!(shellScript && shellScript.dataset.av5TrackRecent === 'true');
  var BOOK_KEY = 'ancona:v2:client-book';

  /* ---- Presentation policy (one JSON parse, exact product IDs) ------------ */
  function cleanProductId(value) {
    var id = String(value == null ? '' : value).trim();
    return /^\d+$/.test(id) ? id : '';
  }

  var policyAvailable = false;
  var excludedProductIds = Object.create(null);
  var policyNode = document.querySelector('[data-ancona-presentation-policy]');
  if (policyNode) {
    try {
      var policyValues = JSON.parse(policyNode.textContent || '[]');
      if (Array.isArray(policyValues)) {
        policyAvailable = true;
        for (var policyIndex = 0; policyIndex < policyValues.length; policyIndex += 1) {
          var excludedId = cleanProductId(policyValues[policyIndex]);
          if (!excludedId) {
            policyAvailable = false;
            excludedProductIds = Object.create(null);
            break;
          }
          excludedProductIds[excludedId] = true;
        }
      }
    } catch (error) {
      policyAvailable = false;
      excludedProductIds = Object.create(null);
    }
  }

  function presentationDecision(productId) {
    var id = cleanProductId(productId);
    if (!policyAvailable || !id) return 'unknown';
    return excludedProductIds[id] ? 'deny' : 'allow';
  }

  var presentationPolicy = {
    available: policyAvailable,
    decision: presentationDecision,
    allows: function (productId) {
      return presentationDecision(productId) === 'allow';
    },
    denies: function (productId) {
      return presentationDecision(productId) === 'deny';
    }
  };

  function applyPresentationPolicy(scope) {
    var root = scope && scope.querySelectorAll ? scope : document;
    var controls = root.querySelectorAll('[data-av5-book]');
    for (var i = 0; i < controls.length; i += 1) {
      if (!presentationPolicy.allows(controls[i].dataset.av5ProductId)) {
        controls[i].setAttribute('hidden', '');
      }
    }
  }

  /* ---- Event bus ---------------------------------------------------------- */
  var bus = {
    on: function (name, handler) {
      document.addEventListener(name, handler);
      return function () { document.removeEventListener(name, handler); };
    },
    off: function (name, handler) {
      document.removeEventListener(name, handler);
    },
    emit: function (name, detail) {
      document.dispatchEvent(new CustomEvent(name, { detail: detail || {} }));
    }
  };

  /* ---- Focus trap (single implementation) --------------------------------- */
  var FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), ' +
    'select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]), ' +
    'summary, iframe';

  function focusables(container) {
    var list = [];
    var nodes = container.querySelectorAll(FOCUSABLE);
    for (var i = 0; i < nodes.length; i += 1) {
      var el = nodes[i];
      if (el.offsetParent !== null || el === document.activeElement) list.push(el);
    }
    return list;
  }

  function trapFocus(container, initial) {
    var target = initial || container.querySelector('[data-av5-dialog-initial]') || focusables(container)[0] || container;
    if (target === container && !container.hasAttribute('tabindex')) container.setAttribute('tabindex', '-1');
    target.focus();

    function onKeydown(event) {
      if (event.key !== 'Tab') return;
      var items = focusables(container);
      if (!items.length) return;
      var first = items[0];
      var last = items[items.length - 1];
      if (event.shiftKey && (document.activeElement === first || document.activeElement === container)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    container.addEventListener('keydown', onKeydown);
    return function release() {
      container.removeEventListener('keydown', onKeydown);
    };
  }

  /* ---- Live-region announcer ---------------------------------------------- */
  var regions = {};

  function liveRegion(tone) {
    var key = tone === 'assertive' ? 'assertive' : 'polite';
    if (!regions[key] || !regions[key].isConnected) {
      var el = document.createElement('div');
      el.className = 'av5-live-region';
      el.setAttribute('aria-live', key);
      el.setAttribute('role', key === 'assertive' ? 'alert' : 'status');
      document.body.appendChild(el);
      regions[key] = el;
    }
    return regions[key];
  }

  function announce(message, tone) {
    if (!message) return;
    var region = liveRegion(tone);
    region.textContent = '';
    window.setTimeout(function () { region.textContent = String(message); }, 40);
  }

  /* ---- Dialog manager for [data-av5-dialog] ------------------------------- */
  var dialogStack = []; /* [{ root, opener, release }] */

  function syncDialogBodyLock() {
    var nativeDialogOpen = document.querySelector('dialog[open]');
    var managedDialogOpen = document.querySelector(
      '[data-av5-dialog]:not(dialog)[role="dialog"][aria-modal="true"]:not([hidden])'
    );
    document.body.classList.toggle(
      'av5-dialog-open',
      !!(nativeDialogOpen || managedDialogOpen)
    );
  }

  function scheduleDialogBodyLockSync() {
    window.queueMicrotask(syncDialogBodyLock);
    window.requestAnimationFrame(syncDialogBodyLock);
  }

  function dialogRoot(name) {
    return document.querySelector('[data-av5-dialog="' + name + '"]');
  }

  function openDialog(name, opener) {
    var root = typeof name === 'string' ? dialogRoot(name) : name;
    if (!root || !root.hasAttribute('hidden')) return false;
    root.removeAttribute('hidden');
    if (!root.hasAttribute('role')) root.setAttribute('role', 'dialog');
    root.setAttribute('aria-modal', 'true');
    var release = trapFocus(root);
    dialogStack.push({ root: root, opener: opener || null, release: release });
    document.body.classList.add('av5-dialog-open');
    bus.emit('ancona:v5:dialog-open', { name: root.getAttribute('data-av5-dialog') });
    return true;
  }

  function closeTopDialog() {
    var entry = dialogStack.pop();
    if (!entry) return false;
    entry.release();
    entry.root.setAttribute('hidden', '');
    scheduleDialogBodyLockSync();
    if (entry.opener && entry.opener.isConnected) entry.opener.focus();
    bus.emit('ancona:v5:dialog-close', { name: entry.root.getAttribute('data-av5-dialog') });
    return true;
  }

  function closeDialog(root) {
    for (var i = dialogStack.length - 1; i >= 0; i -= 1) {
      if (dialogStack[i].root === root) {
        while (dialogStack.length > i) closeTopDialog();
        return true;
      }
    }
    return false;
  }

  document.addEventListener('click', function (event) {
    if (!(event.target instanceof Element)) return;
    var openTrigger = event.target.closest('[data-av5-dialog-open]');
    if (openTrigger) {
      var opened = openDialog(openTrigger.getAttribute('data-av5-dialog-open'), openTrigger);
      if (opened) event.preventDefault();
      return;
    }
    var closeTrigger = event.target.closest('[data-av5-dialog-close]');
    if (closeTrigger) {
      var root = closeTrigger.closest('[data-av5-dialog]');
      if (root && closeDialog(root)) event.preventDefault();
      return;
    }
    /* Backdrop: the dialog root itself is the translucent overlay. */
    if (event.target instanceof Element && event.target.hasAttribute('data-av5-dialog')) {
      closeDialog(event.target);
    }
  });

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape' && dialogStack.length) {
      event.preventDefault();
      closeTopDialog();
    }
  });

  /* ---- Client Book: deferred module loader + header badge ----------------- */
  var bookPromise = null;

  function clientBookUrl() {
    if (CLIENT_BOOK_SRC) return CLIENT_BOOK_SRC;
    if (!SCRIPT_SRC) return 'ancona-v5-client-book.js';
    return SCRIPT_SRC.replace(/ancona-v5-shell\.js/, 'ancona-v5-client-book.js');
  }

  /* Deferred load via an injected classic <script> tag — NOT dynamic import().
     Shopify's asset pipeline rewrites import() into a CommonJS require(), which
     throws "require is not defined" at runtime (measured on the V5 draft). The
     Client Book module self-boots and publishes window.AnconaClientBook, so a
     plain script tag gives the same deferred, non-blocking behavior. */
  function loadClientBook() {
    if (!bookPromise) {
      bookPromise = new Promise(function (resolve, reject) {
        if (window.AnconaClientBook) {
          resolve(window.AnconaClientBook);
          return;
        }
        var tag = document.createElement('script');
        tag.src = clientBookUrl();
        tag.defer = true;
        tag.setAttribute('data-av5-client-book', '');
        tag.onload = function () {
          bus.emit('ancona:v5:book-loaded', {});
          resolve(window.AnconaClientBook || null);
        };
        tag.onerror = function () {
          bookPromise = null; /* allow retry on next interaction */
          if (tag.parentNode) tag.parentNode.removeChild(tag);
          if (window.console && console.error) console.error('Ancona V5: Client Book module failed to load');
          reject(new Error('client-book-load-failed'));
        };
        document.head.appendChild(tag);
      });
    }
    return bookPromise;
  }

  var conciergePromise = null;
  var conciergeCssPromise = null;
  var conciergeMarkupPromise = null;

  function setConciergeLoadState(state) {
    var launchers = document.querySelectorAll('[data-av5-concierge-open]');
    for (var index = 0; index < launchers.length; index += 1) {
      launchers[index].dataset.av5LoadState = state;
      if (state === 'loading') launchers[index].setAttribute('aria-busy', 'true');
      else launchers[index].removeAttribute('aria-busy');
      if (state === 'ready') launchers[index].setAttribute('aria-controls', 'AnconaV5Concierge');
      else launchers[index].removeAttribute('aria-controls');
    }
  }

  function conciergeUrl() {
    if (CONCIERGE_SRC) return CONCIERGE_SRC;
    if (!SCRIPT_SRC) return 'ancona-concierge-core.js';
    return SCRIPT_SRC.replace(/ancona-v5-shell\.js/, 'ancona-concierge-core.js');
  }

  function conciergeCssUrl() {
    if (CONCIERGE_CSS) return CONCIERGE_CSS;
    if (!SCRIPT_SRC) return 'ancona-v5-concierge.css';
    return SCRIPT_SRC.replace(/ancona-v5-shell\.js/, 'ancona-v5-concierge.css');
  }

  function conciergeSectionUrl(host) {
    var sectionId = host && host.dataset
      ? String(host.dataset.av5ConciergeSectionId || '').trim()
      : '';
    if (!/^[a-z0-9][a-z0-9_-]{0,79}$/i.test(sectionId)) return '';
    var localeRoot = window.Shopify &&
      window.Shopify.routes &&
      typeof window.Shopify.routes.root === 'string'
      ? window.Shopify.routes.root
      : '/';
    var url = new URL(localeRoot, window.location.origin);
    url.searchParams.set('section_id', sectionId);

    /* Keep draft previews on the same preview theme without copying arbitrary
       storefront query parameters into the section request. */
    var currentUrl = new URL(window.location.href);
    var previewThemeId = currentUrl.searchParams.get('preview_theme_id') || '';
    if (/^\d+$/.test(previewThemeId)) {
      url.searchParams.set('preview_theme_id', previewThemeId);
    }
    return url.toString();
  }

  function syncConciergeProductContext(root) {
    if (!root) return root;
    var launcher = document.querySelector(
      '.av5-concierge-launcher[data-av5-product-id][data-av5-product-handle][data-av5-product-title]'
    );
    if (!launcher) return root;
    var id = cleanProductId(launcher.dataset.av5ProductId);
    var handle = String(launcher.dataset.av5ProductHandle || '').trim();
    var title = String(launcher.dataset.av5ProductTitle || '').trim().slice(0, 180);
    if (!id || !/^[a-z0-9][a-z0-9-]{0,254}$/i.test(handle) || !title) return root;
    root.dataset.currentProductId = id;
    root.dataset.currentProductHandle = handle;
    root.dataset.currentProductTitle = title;
    return root;
  }

  function loadConciergeMarkup() {
    var existingRoot = document.querySelector('[data-av5-concierge-root]');
    if (existingRoot) return Promise.resolve(syncConciergeProductContext(existingRoot));
    if (!conciergeMarkupPromise) {
      var host = document.querySelector('[data-av5-concierge-host]');
      var url = conciergeSectionUrl(host);
      if (!host || !url || typeof window.fetch !== 'function') {
        return Promise.reject(new Error('concierge-section-unavailable'));
      }
      conciergeMarkupPromise = window.fetch(url, {
          credentials: 'same-origin',
          headers: { 'X-Requested-With': 'XMLHttpRequest' }
        })
          .then(function (response) {
            if (!response.ok) throw new Error('concierge-section-http-' + response.status);
            return response.text();
          })
          .then(function (html) {
            var source = String(html || '').trim();
            if (!source || source === 'null') throw new Error('concierge-section-empty');
            var range = document.createRange();
            range.selectNode(host);
            var fragment = range.createContextualFragment(source);
            var root = fragment.querySelector('[data-av5-concierge-root]');
            if (!root) throw new Error('concierge-section-root-missing');
            host.replaceChildren(fragment);
            return syncConciergeProductContext(
              host.querySelector('[data-av5-concierge-root]')
            );
          })
          .catch(function (error) {
            conciergeMarkupPromise = null;
            host.replaceChildren();
            throw error;
          });
    }
    return conciergeMarkupPromise;
  }

  function loadConciergeCss() {
    if (!conciergeCssPromise) {
      conciergeCssPromise = new Promise(function (resolve, reject) {
        var href = conciergeCssUrl();
        if (!href) {
          resolve();
          return;
        }
        var existing = document.querySelector('link[data-av5-concierge-css]');
        if (existing) {
          if (existing.sheet) resolve();
          else {
            existing.addEventListener('load', resolve, { once: true });
            existing.addEventListener('error', reject, { once: true });
          }
          return;
        }
        var link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = href;
        link.setAttribute('data-av5-concierge-css', '');
        link.onload = resolve;
        link.onerror = function () {
          conciergeCssPromise = null;
          if (link.parentNode) link.parentNode.removeChild(link);
          reject(new Error('concierge-css-load-failed'));
        };
        document.head.appendChild(link);
      });
    }
    return conciergeCssPromise;
  }

  function loadConcierge() {
    if (!conciergePromise) {
      setConciergeLoadState('loading');
      conciergePromise = Promise.all([loadConciergeMarkup(), loadConciergeCss()])
        .then(function () {
          return new Promise(function (resolve, reject) {
            if (window.AnconaConcierge) {
              resolve(window.AnconaConcierge);
              return;
            }
            var tag = document.createElement('script');
            tag.src = conciergeUrl();
            tag.defer = true;
            tag.setAttribute('data-av5-concierge', '');
            tag.onload = function () {
              resolve(window.AnconaConcierge || null);
            };
            tag.onerror = function () {
              if (tag.parentNode) tag.parentNode.removeChild(tag);
              reject(new Error('concierge-load-failed'));
            };
            document.head.appendChild(tag);
          });
        })
        .then(function (api) {
          if (!api) throw new Error('concierge-api-unavailable');
          setConciergeLoadState('ready');
          bus.emit('ancona:v5:concierge-loaded', {});
          return api;
        })
        .catch(function (error) {
          setConciergeLoadState('error');
          conciergePromise = null;
          throw error;
        });
    }
    return conciergePromise;
  }

  function conciergeTrigger(event) {
    return event.target instanceof Element && event.target.closest('[data-av5-concierge-open]');
  }

  function closeClientBookForConciergeHandoff(trigger) {
    if (
      !trigger ||
      typeof trigger.closest !== 'function' ||
      !trigger.closest('[data-av5-book-root]')
    ) return;
    var clientBook = window.AnconaClientBook;
    if (clientBook && typeof clientBook.close === 'function') {
      clientBook.close({ restoreFocus: false });
    }
  }

  document.addEventListener('click', function (event) {
    var trigger = conciergeTrigger(event);
    if (!trigger) return;
    event.preventDefault();
    loadConcierge()
      .then(function (api) {
        if (api && typeof api.open === 'function') {
          closeClientBookForConciergeHandoff(trigger);
          api.open(trigger);
          return;
        }
        throw new Error('concierge-api-unavailable');
      })
      .catch(function () {
        announce('The shopping guide is unavailable right now. Opening direct contact instead.', 'polite');
        var launcher = document.querySelector('.av5-concierge-launcher[href]');
        var href = trigger.getAttribute('href') || (launcher && launcher.getAttribute('href'));
        if (href) window.location.assign(href);
      });
  });

  function readStoredBook() {
    try {
      var raw = window.localStorage.getItem(BOOK_KEY);
      if (!raw || raw.length > 200000) return null;
      var data = JSON.parse(raw);
      if (!data || data.v !== 2 || !Array.isArray(data.saved)) return null;
      return data;
    } catch (error) {
      return null;
    }
  }

  function readBookCount() {
    try {
      var data = readStoredBook();
      if (!data) return 0;
      var ids = data.ids && typeof data.ids === 'object' ? data.ids : {};
      var count = 0;
      for (var i = 0; i < data.saved.length; i += 1) {
        var entry = data.saved[i];
        if (!entry || typeof entry !== 'object') continue;
        var handle = typeof entry.h === 'string' ? entry.h : '';
        var productId = entry.pid || entry.productId || ids[handle] || '';
        if (presentationPolicy.allows(productId)) count += 1;
      }
      return count;
    } catch (error) {
      return 0; /* storage denied or malformed: badge stays hidden */
    }
  }

  /* Paint persisted Save state before the deferred Client Book bundle arrives.
     This prevents a cold “Save” control from silently acting as “Remove.” The
     module revalidates and takes over once loaded; this is a schema-bounded
     first-paint mirror only. */
  function syncColdBookButtons(scope) {
    if (window.AnconaClientBook) return;
    var root = scope && scope.querySelectorAll ? scope : document;
    var controls = root.querySelectorAll('[data-av5-book]');
    var data = readStoredBook();
    var saved = data && Array.isArray(data.saved) ? data.saved : [];
    var ids = data && data.ids && typeof data.ids === 'object' ? data.ids : {};

    for (var index = 0; index < controls.length; index += 1) {
      var control = controls[index];
      var handle = String(control.dataset.av5Handle || '');
      var variantId = String(control.dataset.av5VariantId || '');
      var productId = cleanProductId(control.dataset.av5ProductId);
      var pressed = false;

      for (var savedIndex = 0; savedIndex < saved.length; savedIndex += 1) {
        var entry = saved[savedIndex];
        if (!entry || typeof entry !== 'object' || String(entry.h || '') !== handle) continue;
        var entryProductId = cleanProductId(entry.pid || entry.productId || ids[handle]);
        var entryVariantId = String(entry.vid || entry.variantId || '');
        if (
          productId &&
          entryProductId === productId &&
          (!variantId || entryVariantId === variantId || entryVariantId === '')
        ) {
          pressed = true;
          break;
        }
      }

      control.setAttribute('aria-pressed', pressed ? 'true' : 'false');
      control.classList.toggle('is-saved', pressed);
      var label = control.querySelector('[data-av5-book-label]');
      if (label) {
        label.textContent = pressed
          ? (control.dataset.av5LabelSaved || label.textContent)
          : (control.dataset.av5LabelSave || label.textContent);
      }
    }
  }

  function updateBookBadges(count) {
    var value = typeof count === 'number' ? count : readBookCount();
    var badges = document.querySelectorAll('[data-av5-book-count]');
    for (var i = 0; i < badges.length; i += 1) {
      badges[i].textContent = value > 99 ? '99+' : String(value);
      if (value > 0) badges[i].removeAttribute('hidden');
      else badges[i].setAttribute('hidden', '');
    }
  }

  function bookCountFromApi() {
    try {
      if (window.AnconaClientBook && typeof window.AnconaClientBook.list === 'function') {
        var list = window.AnconaClientBook.list();
        if (Array.isArray(list)) return list.length;
      }
    } catch (error) { /* fall back to storage read */ }
    return null;
  }

  document.addEventListener('ancona:v5:book-change', function () {
    var apiCount = bookCountFromApi();
    updateBookBadges(apiCount === null ? undefined : apiCount);
  });

  window.addEventListener('storage', function (event) {
    if (event && event.key === BOOK_KEY) {
      updateBookBadges();
      syncColdBookButtons(document);
    }
  });
  document.addEventListener('ancona:v5:variant-context', function () {
    syncColdBookButtons(document);
  });
  document.addEventListener('ancona:v5:grid-rendered', function () {
    syncColdBookButtons(document);
  });
  document.addEventListener('ancona:v5:product-swapped', function (event) {
    var root = event.detail && event.detail.root;
    if (!root || !root.querySelectorAll) return;
    applyPresentationPolicy(root);
    syncColdBookButtons(root);
    trackRecentContext(root);
  });

  /* First interaction with any Client Book control loads the module early
     (pointerdown fires before click, hiding the import latency). */
  document.addEventListener('pointerdown', function (event) {
    if (!(event.target instanceof Element)) return;
    var control = event.target.closest(
      '[data-av5-book], [data-av5-book-open], [data-av5-compare], [data-av5-compare-toggle]'
    );
    if (!control) return;
    if (control.hasAttribute('data-av5-book') && !presentationPolicy.allows(control.dataset.av5ProductId)) return;
    loadClientBook().catch(function () {});
  }, { capture: true, passive: true });

  document.addEventListener('keydown', function (event) {
    if (event.key !== ' ' || window.AnconaClientBook) return;
    var trigger = event.target instanceof Element && event.target.closest('a[role="button"][data-av5-book]');
    if (!trigger) return;
    event.preventDefault();
    trigger.click();
  });

  /* Preserve the first cold Save click while the deferred module loads. */
  document.addEventListener('click', function (event) {
    var trigger = event.target instanceof Element && event.target.closest('[data-av5-book]');
    if (!trigger) return;
    event.preventDefault();
    event.anconaV5BookToggleHandled = true;
    var desiredPressed = trigger.getAttribute('aria-pressed') !== 'true';
    if (
      trigger.hasAttribute('data-av5-invalid-variant') ||
      trigger.getAttribute('aria-disabled') === 'true' ||
      !presentationPolicy.allows(trigger.dataset.av5ProductId)
    ) return;
    loadClientBook()
      .then(function (api) {
        if (!api || typeof api.toggle !== 'function') return;
        if (typeof api.isSaved === 'function') {
          var actualPressed = api.isSaved(trigger.dataset.av5Handle, trigger.dataset.av5VariantId || '');
          if (actualPressed === desiredPressed) return;
        }
        api.toggle(trigger);
      })
      .catch(function () {
        announce('The Client Book is unavailable right now. Shopping is unaffected.', 'polite');
      });
  });

  /* The header trigger opens the Client Book drawer once the module is in. */
  document.addEventListener('click', function (event) {
    var trigger = event.target instanceof Element && event.target.closest('[data-av5-book-open]');
    if (!trigger) return;
    event.preventDefault();
    event.anconaV5BookOpenHandled = true;
    loadClientBook()
      .then(function (api) {
        if (api && typeof api.open === 'function') {
          api.open(trigger);
          return;
        }
        bus.emit('ancona:v5:book-open', { source: 'header', opener: trigger });
      })
      .catch(function () {
        announce('The Client Book is unavailable right now. Shopping is unaffected.', 'polite');
      });
  });

  /* ---- Metrics: dataLayer init + ancona:* republish (V4 names kept) ------- */
  try { window.dataLayer = window.dataLayer || []; } catch (error) { /* never break the page */ }

  var METRIC_EVENTS = [
    /* V4 vocabulary (ancona-metrics.js) — names unchanged */
    'saved_add', 'saved_remove', 'shortlist_share', 'return_to_results',
    'recently_viewed_clear', 'guide_toc_click',
    'saved_open', 'compare_add', 'compare_remove', 'compare_open',
    'inquiry_start', 'inquiry_context',
    'product_print', 'guide_print', 'guide_share',
    'sticky_add_to_cart', 'cta_click', 'recs_fallback_shown',
    'saved_cart_add', 'saved_refresh', 'shortlist_view', 'saved_update',
    /* V5 additions (non-sensitive identifiers only, enforced upstream) */
    'v5:book-change', 'v5:book-open'
  ];

  METRIC_EVENTS.forEach(function (name) {
    document.addEventListener('ancona:' + name, function (event) {
      try {
        var detail = event && event.detail ? event.detail : {};
        /* Cross-tab mirror events are UI refresh signals, not shopper KPIs. */
        if (detail.source === 'tabsync') return;
        if (!(window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function')) return;
        window.Shopify.analytics.publish('ancona:' + name, detail);
      } catch (error) { /* analytics must never break shopping */ }
    });
  });

  /* ---- Product-aware contact handoff ------------------------------------- */
  function cleanContactContext(value, limit) {
    return String(value == null ? '' : value)
      .replace(/[\u0000-\u001f\u007f]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, limit || 300);
  }

  function applyContactProductContext() {
    var params;
    try {
      params = new URLSearchParams(window.location.search);
    } catch (error) {
      return;
    }
    var handles = [];
    var primaryHandle = cleanRecentHandle(params.get('product'));
    if (primaryHandle) handles.push(primaryHandle);
    String(params.get('products') || '').split(',').slice(0, 4).forEach(function (value) {
      var handle = cleanRecentHandle(value);
      if (handle && handles.indexOf(handle) === -1 && handles.length < 4) handles.push(handle);
    });
    var question = cleanContactContext(params.get('question'), 300);
    var request = cleanContactContext(params.get('request'), 300);
    var listedFacts = cleanContactContext(params.get('listed_facts'), 500);
    if (!handles.length && !question && !request && !listedFacts) return;

    var forms = document.querySelectorAll('form[action*="/contact"]');
    var contactForm = null;
    var questionField = null;
    for (var formIndex = 0; formIndex < forms.length; formIndex += 1) {
      questionField = forms[formIndex].querySelector('textarea[name^="contact["]');
      if (questionField) {
        contactForm = forms[formIndex];
        break;
      }
    }
    if (!contactForm || contactForm.dataset.av5ProductContextState) return;
    contactForm.dataset.av5ProductContextState = handles.length ? 'loading' : 'context';

    function appendHidden(key, name, value) {
      if (!value) return;
      var input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = value;
      input.dataset.av5ContactContextField = key;
      contactForm.appendChild(input);
    }

    if (question) appendHidden('question', 'contact[Concierge question]', question);
    if (request) appendHidden('request', 'contact[Concierge request]', request);
    if (listedFacts) {
      appendHidden(
        'listed-facts',
        'contact[Listed product facts — verify]',
        listedFacts
      );
    }

    var visiblePrefill = [];
    if (question) visiblePrefill.push(question);
    if (request && request !== question) visiblePrefill.push('Request: ' + request);
    if (listedFacts) visiblePrefill.push('Listed facts for Ancona to verify: ' + listedFacts);
    if (!questionField.value.trim() && visiblePrefill.length) {
      questionField.value = visiblePrefill.join('\n\n').slice(0, 1800);
    }

    var storefrontRoot = window.Shopify && window.Shopify.routes && window.Shopify.routes.root
      ? window.Shopify.routes.root
      : '/';
    var controller = new AbortController();
    var timeoutId = window.setTimeout(function () { controller.abort(); }, 8000);

    function verifyHandle(handle) {
      var productUrl = new URL(
        String(storefrontRoot).replace(/\/?$/, '/') + 'products/' + handle,
        window.location.origin
      );
      var productEndpoint = new URL(productUrl.href);
      productEndpoint.pathname += '.js';
      return fetch(productEndpoint.href, {
        method: 'GET',
        credentials: 'same-origin',
        cache: 'no-store',
        redirect: 'error',
        headers: { Accept: 'application/json' },
        signal: controller.signal
      }).then(function (response) {
        if (!response.ok) throw new Error('product-context-request-failed');
        var contentLength = Number(response.headers.get('content-length') || 0);
        if (contentLength > 2000000) throw new Error('product-context-response-too-large');
        return response.text().then(function (text) {
          if (text.length > 2000000) throw new Error('product-context-response-too-large');
          return JSON.parse(text);
        });
      }).then(function (productData) {
        var verifiedId = cleanProductId(productData && productData.id);
        var verifiedHandle = cleanRecentHandle(productData && productData.handle);
        var verifiedTitle = cleanContactContext(productData && productData.title, 180);
        if (
          !verifiedId ||
          verifiedHandle !== handle ||
          !verifiedTitle ||
          !presentationPolicy.allows(verifiedId)
        ) throw new Error('product-context-verification-failed');
        return {
          id: verifiedId,
          handle: verifiedHandle,
          title: verifiedTitle,
          url: new URL(
            String(storefrontRoot).replace(/\/?$/, '/') + 'products/' + verifiedHandle,
            window.location.origin
          )
        };
      });
    }

    var verification = handles.length
      ? Promise.all(handles.map(function (handle) {
          return verifyHandle(handle).catch(function () { return null; });
        }))
      : Promise.resolve([]);

    verification.then(function (results) {
      var verifiedProducts = results.filter(Boolean);
      verifiedProducts.forEach(function (product, index) {
        var suffix = verifiedProducts.length === 1 ? '' : ' ' + (index + 1);
        appendHidden(
          'product-title-' + index,
          'contact[Product' + suffix + ' title]',
          product.title
        );
        appendHidden(
          'product-handle-' + index,
          'contact[Product' + suffix + ' handle]',
          product.handle
        );
        appendHidden(
          'product-url-' + index,
          'contact[Product' + suffix + ' URL]',
          product.url.href
        );
      });
      appendHidden(
        'inquiry-type',
        'contact[Concierge inquiry type]',
        verifiedProducts.length ? 'Product question' : 'Concierge request'
      );
      appendHidden(
        'concierge-context',
        'contact[Concierge context]',
        verifiedProducts.length > 1 ? 'Ask about these pieces' : 'Ask about this piece'
      );

      var notice = document.createElement('p');
      notice.id = 'AnconaContactProductContext';
      notice.className = 'av5-contact-product-context';
      notice.setAttribute('role', 'status');
      if (verifiedProducts.length) {
        notice.appendChild(document.createTextNode('Your request includes '));
        verifiedProducts.forEach(function (product, index) {
          if (index) notice.appendChild(document.createTextNode(index === verifiedProducts.length - 1 ? ' and ' : ', '));
          var productLink = document.createElement('a');
          productLink.href = product.url.pathname;
          productLink.textContent = product.title;
          notice.appendChild(productLink);
        });
        notice.appendChild(document.createTextNode(
          '. Review or edit your question below; Ancona will confirm any listed facts or physical fit.'
        ));
        if (verifiedProducts.length < handles.length) {
          notice.appendChild(document.createTextNode(
            ' One or more requested product links could not be revalidated and were not attached.'
          ));
        }
      } else if (handles.length) {
        notice.textContent =
          'The requested product context could not be revalidated, so no product was attached. Paste the product link into your question if you still want Ancona to review it.';
      } else {
        notice.textContent =
          'Your Concierge request is ready for review. Edit it below before sending it through Shopify’s contact form.';
      }
      contactForm.insertBefore(notice, contactForm.firstChild);
      if (!questionField.getAttribute('placeholder')) {
        questionField.setAttribute('placeholder', 'What would you like Ancona to confirm or create?');
      }
      var describedBy = String(questionField.getAttribute('aria-describedby') || '').trim();
      questionField.setAttribute(
        'aria-describedby',
        (describedBy ? describedBy + ' ' : '') + notice.id
      );
      questionField.dataset.av5ProductQuestion = verifiedProducts.length ? 'true' : 'false';
      contactForm.dataset.av5ProductContextState =
        verifiedProducts.length === handles.length ? 'verified' : (verifiedProducts.length ? 'partial' : 'context');
    }).catch(function () {
      contactForm.dataset.av5ProductContextState = 'unavailable';
      var notice = document.createElement('p');
      notice.className = 'av5-contact-product-context';
      notice.setAttribute('role', 'status');
      notice.textContent =
        'The Concierge context could not be prepared automatically. Nothing was submitted; add the product link and request manually.';
      contactForm.insertBefore(notice, contactForm.firstChild);
    }).finally(function () {
      window.clearTimeout(timeoutId);
    });
  }

  /* Keep the compact mobile launcher away from visible purchase/navigation
     controls. This is an event-driven, bounded placement check: no polling,
     MutationObserver, or page-wide layout scan. */
  var launcherCollisionFrame = 0;
  var launcherCollisionBound = false;
  var FLOATING_LAUNCHERS = '.av5-concierge-launcher, .av5-client-book-launcher';
  var LAUNCHER_CRITICAL =
    '.product-form__submit, .shopify-payment-button, .shopify-payment-button__button, ' +
    'shopify-accelerated-checkout, shopify-buy-it-now-button, ' +
    '.cart__dynamic-checkout-buttons, #dynamic-checkout-cart, [data-shopify-buttoncontainer], ' +
    '[data-av5-sticky-atc], cart-drawer.active, cart-drawer[open], ' +
    'cart-notification[open], #shopify-pc__banner, #shopify-pc__prefs, ' +
    '.shopify-pc__banner__dialog, #shop-hcaptcha-badge-container, ' +
    '[data-av5-book-root][open], ' +
    'dialog[open]:not(.av5-concierge)';

  function rectsOverlap(first, second) {
    return !(
      first.right <= second.left ||
      first.left >= second.right ||
      first.bottom <= second.top ||
      first.top >= second.bottom
    );
  }

  function launcherCandidateRect(name, width, height) {
    var edge = 12;
    /* Match the mobile CSS raised position. The 92px offset clears the
       78px sticky purchase bar with a deliberate visual/touch separation. */
    var raised = 92;
    var isProduct = document.body.classList.contains('template-product');
    var isCart = document.body.classList.contains('template-cart');
    /* Product pages reserve the sticky purchase bar from first paint. The
       sticky bar changes visibility after its observer runs, so waiting for a
       scroll/resize event can briefly leave the launcher overlapping it. */
    var defaultBottom = isProduct ? raised : (isCart ? 72 : edge);
    var left = /left/.test(name) ? edge : window.innerWidth - edge - width;
    var top;
    if (/middle/.test(name)) top = (window.innerHeight - height) / 2;
    else {
      var bottom = /raised/.test(name) ? raised : defaultBottom;
      top = window.innerHeight - bottom - height;
    }
    return {
      left: left,
      top: top,
      right: left + width,
      bottom: top + height
    };
  }

  function setFloatingLaunchersSuppressed(suppressed) {
    var active = document.activeElement;
    Array.prototype.forEach.call(document.querySelectorAll(FLOATING_LAUNCHERS), function (control) {
      var hasFocus = active && (active === control || control.contains(active));

      /* Do not remove the focused control from the accessibility tree. A
         document-level focusout hook immediately retries suppression after
         focus moves elsewhere. */
      if (suppressed && hasFocus) {
        control.removeAttribute('data-av5-launcher-suppressed');
        control.setAttribute('data-av5-launcher-suppression-pending', 'true');
        return;
      }

      control.removeAttribute('data-av5-launcher-suppression-pending');
      if (suppressed) control.setAttribute('data-av5-launcher-suppressed', 'true');
      else control.removeAttribute('data-av5-launcher-suppressed');
    });
  }

  function placeMobileLauncher() {
    launcherCollisionFrame = 0;
    var launcher = document.querySelector('.av5-concierge-launcher');
    if (!window.matchMedia('(max-width: 749px)').matches) {
      setFloatingLaunchersSuppressed(false);
      if (launcher) launcher.removeAttribute('data-av5-launcher-placement');
      return;
    }
    if (!launcher) return;

    var launcherRect = launcher.getBoundingClientRect();
    if (!launcherRect.width || !launcherRect.height) return;
    var criticalRects = Array.prototype.slice.call(document.querySelectorAll(LAUNCHER_CRITICAL))
      .filter(function (element) {
        if (launcher.contains(element) || element.contains(launcher)) return false;
        var style = window.getComputedStyle(element);
        return style.display !== 'none' && style.visibility !== 'hidden' && element.getClientRects().length > 0;
      })
      .map(function (element) { return element.getBoundingClientRect(); });
    var candidates = ['right', 'left', 'right-raised', 'left-raised', 'right-middle', 'left-middle'];
    var best = candidates[0];
    var bestCount = Number.POSITIVE_INFINITY;
    for (var index = 0; index < candidates.length; index += 1) {
      var candidateRect = launcherCandidateRect(
        candidates[index],
        launcherRect.width,
        launcherRect.height
      );
      var count = criticalRects.filter(function (rect) {
        return rectsOverlap(candidateRect, rect);
      }).length;
      if (count < bestCount) {
        best = candidates[index];
        bestCount = count;
      }
      if (count === 0) break;
    }
    if (launcher.dataset.av5LauncherPlacement !== best) {
      launcher.setAttribute('data-av5-launcher-placement', best);
    }
    setFloatingLaunchersSuppressed(bestCount > 0);
  }

  function requestMobileLauncherPlacement() {
    if (launcherCollisionFrame) return;
    launcherCollisionFrame = window.requestAnimationFrame(placeMobileLauncher);
  }

  function bindMobileLauncherCollisionGuard() {
    if (launcherCollisionBound) {
      requestMobileLauncherPlacement();
      return;
    }
    launcherCollisionBound = true;
    window.addEventListener('scroll', requestMobileLauncherPlacement, { passive: true });
    window.addEventListener('resize', requestMobileLauncherPlacement, { passive: true });
    window.addEventListener('orientationchange', requestMobileLauncherPlacement, { passive: true });
    window.addEventListener('load', requestMobileLauncherPlacement, { once: true });
    document.addEventListener('ancona:v5:concierge-close', requestMobileLauncherPlacement);
    document.addEventListener('focusout', function (event) {
      var target = event.target;
      if (
        target &&
        target.closest &&
        target.closest('[data-av5-launcher-suppression-pending="true"]')
      ) {
        requestMobileLauncherPlacement();
      }
    });
    requestMobileLauncherPlacement();
  }

  var sharedBookBooted = false;

  /* Preserve shared V1 shortlist URLs while keeping V5 as the only renderer.
     A shared-link visit is explicit Client Book intent, so this is the sole
     non-click path that loads the deferred module on an ordinary route. */
  function openSharedBookFromLocation() {
    if (sharedBookBooted) return;
    var params;
    try {
      params = new URLSearchParams(window.location.search);
    } catch (error) {
      return;
    }
    var current = params.get('book') || '';
    var legacy = params.get('shortlist') || '';
    var raw = current || legacy;
    if (!raw) return;
    var handles = [];
    raw.split(',').slice(0, 50).forEach(function (value) {
      var handle = cleanRecentHandle(value);
      if (handle && handles.indexOf(handle) === -1) handles.push(handle);
    });
    if (!handles.length) return;
    sharedBookBooted = true;

    if (!current && legacy && window.history && typeof window.history.replaceState === 'function') {
      try {
        params.set('book', handles.join(','));
        params.delete('shortlist');
        var nextUrl = window.location.pathname +
          (params.toString() ? '?' + params.toString() : '') +
          window.location.hash;
        window.history.replaceState(window.history.state, '', nextUrl);
      } catch (error) {
        /* The Client Book module also reads shortlist= directly. */
      }
    }

    if (document.querySelector('[data-av5-book-root][data-context="page"]')) return;
    loadClientBook()
      .then(function (api) {
        if (!api || typeof api.open !== 'function') return;
        var opener = document.querySelector('[data-av5-book-open]');
        api.open(opener || null);
      })
      .catch(function () {
        announce('This shared Client Book is unavailable right now. Shopping is unaffected.', 'polite');
      });
  }

  /* ---- Boot (P0-8 pattern: DOMContentLoaded + shopify:section:load) ------- */
  function init() {
    applyPresentationPolicy(document);
    syncColdBookButtons(document);
    updateBookBadges();
    if (document.querySelector('[data-av5-book-root][data-context="page"]')) {
      loadClientBook().catch(function () {
        announce('The Client Book is unavailable right now. Shopping is unaffected.', 'polite');
      });
    }
    if (document.querySelector('[data-av5-concierge-form-return]')) {
      loadConcierge().catch(function () {
        announce('Your contact response is available on the page, but the shopping guide could not open.', 'polite');
      });
    }
    openSharedBookFromLocation();
    applyContactProductContext();
    scheduleRecentTracking();
    bindMobileLauncherCollisionGuard();
  }

  var recentTrackingScheduled = false;
  var MAX_RECENT = 12;

  function cleanRecentHandle(value) {
    var handle = String(value == null ? '' : value).trim().toLowerCase();
    return /^[a-z0-9][a-z0-9-]{0,254}$/.test(handle) ? handle : '';
  }

  function hasLegacyBookStorage() {
    try {
      return !!(
        window.localStorage.getItem('ancona:v1:saved') ||
        window.localStorage.getItem('ancona:v1:compare') ||
        window.localStorage.getItem('ancona:recently-viewed')
      );
    } catch (error) {
      return true;
    }
  }

  function recentBookSnapshot() {
    try {
      var raw = window.localStorage.getItem(BOOK_KEY);
      if (!raw) {
        if (hasLegacyBookStorage()) return null;
        return { v: 2, saved: [], compare: [], recent: [], ids: Object.create(null) };
      }
      if (raw.length > 200000) return null;
      var data = JSON.parse(raw);
      if (
        !data ||
        data.v !== 2 ||
        !Array.isArray(data.saved) ||
        !Array.isArray(data.compare) ||
        !Array.isArray(data.recent) ||
        data.saved.length > 50 ||
        data.compare.length > 4 ||
        data.recent.length > MAX_RECENT ||
        !data.ids ||
        typeof data.ids !== 'object' ||
        Array.isArray(data.ids)
      ) return null;

      for (var recentIndex = 0; recentIndex < data.recent.length; recentIndex += 1) {
        var entry = data.recent[recentIndex];
        if (
          !entry ||
          typeof entry !== 'object' ||
          !cleanRecentHandle(entry.h) ||
          !cleanProductId(entry.pid) ||
          !Number.isFinite(Number(entry.ts))
        ) return null;
      }

      var ids = Object.create(null);
      var idKeys = Object.keys(data.ids);
      if (idKeys.length > 100) return null;
      for (var idIndex = 0; idIndex < idKeys.length; idIndex += 1) {
        var key = cleanRecentHandle(idKeys[idIndex]);
        var productId = cleanProductId(data.ids[idKeys[idIndex]]);
        if (!key || !productId) return null;
        ids[key] = productId;
      }

      return {
        v: 2,
        saved: data.saved,
        compare: data.compare,
        recent: data.recent,
        ids: ids
      };
    } catch (error) {
      return null;
    }
  }

  function trackRecentContext(scope) {
    if (!TRACK_RECENT || window.AnconaClientBook) return false;
    var target = scope && scope.querySelector ? scope : document;
    var contextNode = target.matches && target.matches('[data-av5-product-context]')
      ? target
      : target.querySelector('[data-av5-product-context]');
    if (!contextNode) return false;

    var context = {};
    try {
      context = JSON.parse(contextNode.textContent || '{}');
    } catch (error) {
      return false;
    }
    var handle = cleanRecentHandle(context.handle);
    var host = contextNode.closest('[data-product-id]');
    var productInfo = document.querySelector('product-info[data-product-id]');
    var productId = cleanProductId(
      context.productId ||
      context.pid ||
      (host && host.dataset.productId) ||
      (productInfo && productInfo.dataset.productId)
    );
    if (!handle || !presentationPolicy.allows(productId)) return false;

    var snapshot = recentBookSnapshot();
    if (!snapshot) return false;
    var alreadyRecent = (
      snapshot.recent[0] &&
      cleanRecentHandle(snapshot.recent[0].h) === handle &&
      cleanProductId(snapshot.recent[0].pid) === productId
    );
    if (alreadyRecent && cleanProductId(snapshot.ids[handle]) === productId) return false;
    if (!alreadyRecent) {
      var recent = [];
      for (var index = 0; index < snapshot.recent.length; index += 1) {
        if (cleanRecentHandle(snapshot.recent[index].h) !== handle) recent.push(snapshot.recent[index]);
      }
      recent.unshift({ h: handle, pid: productId, ts: Date.now() });
      snapshot.recent = recent.slice(0, MAX_RECENT);
    }
    snapshot.ids[handle] = productId;

    try {
      var serialized = JSON.stringify(snapshot);
      if (serialized.length > 200000) return false;
      window.localStorage.setItem(BOOK_KEY, serialized);
      updateBookBadges();
      bus.emit('ancona:v5:book-change', { source: 'recent', recentOnly: true });
      return true;
    } catch (error) {
      return false;
    }
  }

  function scheduleRecentTracking() {
    if (!TRACK_RECENT || recentTrackingScheduled) return;
    recentTrackingScheduled = true;
    var track = function () { trackRecentContext(document); };
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(track, { timeout: 1500 });
    } else {
      window.setTimeout(track, 800);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function () {
    /* Re-rendered header instances need their badge repainted; all other
       wiring is document-delegated and survives section reloads. */
    applyPresentationPolicy(document);
    syncColdBookButtons(document);
    updateBookBadges();
    if (document.querySelector('[data-av5-book-root][data-context="page"]')) {
      loadClientBook().catch(function () {});
    }
    applyContactProductContext();
    scheduleRecentTracking();
    requestMobileLauncherPlacement();
  });

  window.AnconaV5 = {
    bus: bus,
    trapFocus: trapFocus,
    announce: announce,
    dialog: {
      open: openDialog,
      close: closeDialog,
      closeTop: closeTopDialog,
      syncBodyLock: syncDialogBodyLock,
      scheduleBodyLockSync: scheduleDialogBodyLockSync
    },
    presentationPolicy: presentationPolicy,
    loadClientBook: loadClientBook,
    loadConcierge: loadConcierge,
    getClientBookCount: readBookCount,
    updateBookBadges: updateBookBadges
  };
})();
